import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {EnrichSettings, HubspotProperty} from '../../../models/hubspot.model';
import {ClientService} from '../../../services/auth/client.service';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';

import {MaterialModule} from '../../../material.module';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {ActivatedRoute, Router} from '@angular/router';
import {ReferenceService} from '../../../services/enrich/reference.service';
import {EnrichSetupService} from '../../../services/enrich/enrich-setup.service';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';
import {MatTable} from '@angular/material/table';
import {ToastService} from '../../../services/layout/toast.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';

interface FieldMapping {
  sourceField: string;
  targetField: string;
  enabled: boolean;
}

@Component({
  selector: 'app-field-mapping',
  templateUrl: './mapping.component.html',
  styleUrls: ['./mapping.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, BreadcrumbsComponent]
})
export class FieldMappingComponent implements OnInit {
    private fb = inject(FormBuilder);
    private hubspotService = inject(HubspotService);
    private clientService = inject(ClientService);
    private clientLoadingService = inject(ClientLoadingService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private referenceService = inject(ReferenceService);
    private enrichSetupService = inject(EnrichSetupService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);
    private toastService = inject(ToastService);

  mappingForm: FormGroup;
  isLoading: boolean = true;
  isSaving: boolean = false;
  error: string | null = null;
  selectedIndex: number = 0; // Default to the first content tab
  queryParamName: string = 'tab'; // Name of the query parameter for tab index
  @ViewChild(MatTable) table?: MatTable<FormGroup>;
  warningExpanded = false;

  // Source fields from the Norwegian Business Registry
  sourceFields: { value: string, label: string }[] = [];

  // HubSpot properties
  hubspotProperties: HubspotProperty[] = [];

  readonly breadcrumbs = [
    {label: 'Shine Enrich', route: '/enrich/setup'},
    {label: 'Field mapping'}
  ];

    // Standard HubSpot fields that Shine Enrich overwrites on each sync
    readonly overwrittenHubspotFields: { hubspot: string; source: string }[] = [
        {hubspot: 'Name', source: 'company_name'},
        {hubspot: 'Address', source: 'street_address'},
        {hubspot: 'City', source: 'city'},
        {hubspot: 'Country', source: 'country_select'},
        {hubspot: 'Zip / Postal Code', source: 'postal_code'},
      {hubspot: 'Company type', source: 'organization_type'},
        {hubspot: 'Phone', source: 'phone_number or mobile_phone_number'},
        {hubspot: 'Annual revenue', source: 'latest_annual_revenue'},
        {hubspot: 'Org. number property (from Settings)', source: 'company_id'},
    ];

    constructor() {
    this.mappingForm = this.fb.group({
      mappings: this.fb.array([])
    });
  }

  // Flag to track if this component was opened from the setup flow
  isFromSetup: boolean = false;

  ngOnInit(): void {
    // Initialize the selected tab from URL query parameters
    const indexParam = this.route.snapshot.queryParamMap.get(this.queryParamName);
    const parsedIndex = parseInt(indexParam || '', 10);
    this.selectedIndex = !isNaN(parsedIndex) && parsedIndex >= 0 ? parsedIndex : 0;

    // Check if this component was opened from the setup flow
    this.isFromSetup = this.route.snapshot.queryParamMap.get('setup') === 'true';

    // Subscribe to query parameter changes
    this.route.queryParams.subscribe(params => {
      const paramValue = parseInt(params[this.queryParamName] || '', 10);
      this.selectedIndex = !isNaN(paramValue) && paramValue >= 0 ? paramValue : 0;
      this.isFromSetup = params['setup'] === 'true';
    });

      // Use ClientLoadingService to ensure a client is selected
      this.isLoading = true;
      this.error = null;

      this.clientLoadingService.ensureClientSelected().subscribe({
        next: () => {
              // Client is loaded and selected, proceed with loading Brreg fields
              this.loadBrregFields();
          },
          error: (err) => {
              this.enrichDiagnostics.reportError('enrich.mapping.client-selection', err);
              this.error = 'No client selected. Please select a client from the dropdown.';
              this.isLoading = false;
          }
      });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedIndex = event.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.queryParamName]: event.index },
      queryParamsHandling: 'merge'
    });
  }

  get mappings(): FormArray {
    return this.mappingForm.get('mappings') as FormArray;
  }

  loadBrregFields(): void {
    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.referenceService.getBrregFields(clientId).subscribe(
      (fields: string[]) => {
        // Convert string array to array of objects with value and label
        this.sourceFields = fields.map(field => ({
          value: field,
          label: this.formatFieldLabel(field)
        }));
        this.loadHubspotProperties();
      },
        () => {
        this.error = 'Failed to load Brreg fields';
        this.isLoading = false;
      }
    );
  }

  // Format field name to a more readable label (e.g., "company_name" -> "Company Name")
  formatFieldLabel(field: string): string {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  loadHubspotProperties(): void {
    this.error = null;
    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.referenceService.getHubspotProperties(clientId).subscribe(
      (properties: HubspotProperty[]) => {
        this.hubspotProperties = properties;
        this.loadMappings();
      },
        () => {
        this.error = 'Failed to load HubSpot properties';
        this.isLoading = false;
      }
    );
  }

  loadMappings(): void {
    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.hubspotService.getSettings(clientId).subscribe(
      (settings: EnrichSettings) => {
        // Clear existing mappings
        while (this.mappings.length) {
          this.mappings.removeAt(0);
        }

        // If there are existing mappings, add them to the form
        if (settings.mapping && settings.mapping.length > 0) {
          settings.mapping.forEach((mapping: FieldMapping) => {
            this.addMapping(mapping);
          });

          // Ensure Shine Enrich specific fields are included
        } else {
          // Otherwise, just add the required Shine Enrich mapping
          // Find the company_id field
          const companyIdField = this.sourceFields.find(field => field.value === 'company_id');

          if (companyIdField) {
            this.addMapping({
              sourceField: 'company_id',
              targetField: 'shine_enrich_company_id',
              enabled: true
            });
          }
        }

        this.isLoading = false;
        this.table?.renderRows();
      },
        () => {
        this.error = 'Failed to load mappings';
        this.isLoading = false;
      }
    );
  }

  createMappingFormGroup(mapping?: FieldMapping): FormGroup {
    // Allow empty initial rows; we filter empty rows before saving
    const sourceFieldControl = new FormControl(mapping?.sourceField || '');
    const targetFieldControl = new FormControl(mapping?.targetField || '');
    const enabledControl = new FormControl(mapping?.enabled !== undefined ? mapping.enabled : true);

    return this.fb.group({
      sourceField: sourceFieldControl,
      targetField: targetFieldControl,
      enabled: enabledControl,
      sourceFieldFilter: new FormControl(''),
      targetFieldFilter: new FormControl('')
    });
  }

  addMapping(mapping?: FieldMapping): void {
    this.mappings.push(this.createMappingFormGroup(mapping));
    this.mappingForm.markAsDirty();
    this.mappingForm.updateValueAndValidity({emitEvent: false});
    this.table?.renderRows();
  }

  toggleWarning(): void {
    this.warningExpanded = !this.warningExpanded;
  }

  // Filter source fields based on the search term
  filterSourceFields(value: string | null): { value: string, label: string }[] {
    if (!value) {
      return this.sourceFields;
    }

    if (this.sourceFields.some(field => field.value === value)) {
      return this.sourceFields;
    }

    const filterValue = value.toLowerCase();
    return this.sourceFields.filter(field => 
      field.label.toLowerCase().includes(filterValue) || 
      field.value.toLowerCase().includes(filterValue)
    );
  }

  // Filter HubSpot properties based on the search term
  filterHubspotProperties(value: string | null): HubspotProperty[] {
    if (!value) {
      return this.hubspotProperties;
    }
    const filterValue = value.toLowerCase();
    return this.hubspotProperties.filter(prop => 
      prop.label.toLowerCase().includes(filterValue) || 
      prop.name.toLowerCase().includes(filterValue) ||
      prop.groupName.toLowerCase().includes(filterValue)
    );
  }

  removeMapping(index: number): void {
    this.mappings.removeAt(index);
    this.table?.renderRows();
  }

  saveMappings(): void {
    if (this.mappingForm.invalid) {
      this.error = 'Please fix the form errors before saving';
      return;
    }

    this.isSaving = true;
    this.error = null;

    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      this.isSaving = false;
      return;
    }

    this.hubspotService.getSettings(clientId).subscribe(
      (settings: EnrichSettings) => {
        // Get user-defined mappings from the form and filter out any with empty source fields
        const userMappings = this.mappingForm.value.mappings.filter((m: FieldMapping) => m.sourceField && m.sourceField.trim() !== '');

        // Ensure Shine Enrich specific fields are always included
        // Find the company_id field in the user mappings
        const companyIdMapping = userMappings.find((m: FieldMapping) => m.sourceField === 'company_id');

        // If company_id is mapped to a custom field, keep both mappings
        // Otherwise, ensure it's mapped to the standard Shine Enrich field
        if (!companyIdMapping || !companyIdMapping.targetField) {
          // Find if there's already a mapping for company_id to shine_enrich_company_id
          const existingShineMapping = userMappings.find(
            (m: FieldMapping) => m.sourceField === 'company_id' && m.targetField === 'shine_enrich_company_id'
          );

          // If not, add it
          if (!existingShineMapping) {
            userMappings.push({
              sourceField: 'company_id',
              targetField: 'shine_enrich_company_id',
              enabled: true
            });
          }
        }

        // Update the mappings in the settings
        settings.mapping = userMappings;

        this.hubspotService.updateSettings(settings, clientId).subscribe(
            () => {
            // Remove empty mappings from the UI
            for (let i = this.mappings.length - 1; i >= 0; i--) {
              const mapping = this.mappings.at(i).value;
              if (!mapping.sourceField || mapping.sourceField.trim() === '') {
                this.mappings.removeAt(i);
              }
            }
            this.toastService.showSuccess('Mappings saved successfully', 4000);
            this.isSaving = false;

            // If this component was opened from the setup flow, update the setup status
            if (this.isFromSetup) {
              // Update the setup status to indicate that mapping is complete
              if (settings.setupStatus) {
                settings.setupStatus.mappingSetup = true;

                this.hubspotService.updateSettings(settings, clientId).subscribe(
                  () => {
                    this.enrichSetupService.updateReadinessFromSettings(settings);
                    // Redirect back to the setup component
                    this.router.navigate(['/enrich/setup']);
                  },
                    () => {
                    this.error = 'Failed to update setup status';
                  }
                );
              }
            }
          },
            () => {
            this.error = 'Failed to save mappings';
            this.isSaving = false;
          }
        );
      },
        () => {
        this.error = 'Failed to load settings';
        this.isSaving = false;
      }
    );
  }

  getSourceFieldLabel(value: string): string {
    const field = this.sourceFields.find(f => f.value === value);
    return field ? field.label : value;
  }

  displaySourceField = (value: string): string => {
    if (!value) return '';
    const field = this.sourceFields.find(f => f.value === value);
    return field ? field.label : value;
  }

  displayTargetField = (value: string): string => {
    if (!value) return '';
    const property = this.hubspotProperties.find(p => p.name === value);
    return property ? property.label : value;
  }

  /**
   * Confirms that the user is done with mapping and moves to step 6
   */
  confirmAndContinue(): void {
    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isSaving = true;
    this.error = null;
    this.hubspotService.getSettings(clientId).subscribe(
      (settings: EnrichSettings) => {
        if (settings.setupStatus) {
          // Update the setup status to indicate that mapping is complete
          settings.setupStatus.mappingSetup = true;

          this.hubspotService.updateSettings(settings, clientId).subscribe(
              () => {
                this.enrichSetupService.updateReadinessFromSettings(settings);

              // Navigate to the setup component (which will now show step 6)
              this.router.navigate(['/enrich/setup']);

                this.isSaving = false;
              },
              () => {
                this.error = 'Failed to update setup status';
                this.isSaving = false;
              }
          );
        }
      },
        () => {
          this.error = 'Failed to load settings';
          this.isSaving = false;
        }
    );
  }
}
